<template>
  <div>
    <Table
      :columns="columns"
      :rows="partnerOrdersSorted"
      :row-style-class="(row) => getRowStyleClass(row)"
      :sort-options="{
        initialSortBy: { field: 'created_at', type: 'desc' },
      }"
      @on-row-click="onRowClick"
      @on-sort-change="onSortChange"
    />
  </div>
</template>

<script type="text/javascript">
import { mapGetters } from 'vuex';
import Table from '@/components/Table.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import _ from 'lodash';
import Attachment from '@/components/global/Attachment.vue';

export default {
  name: 'PartnersPartnerOrders',
  components: {
    Table,
  },
  data() {
    return {
      sort: [
        { column: 'created_at', descending: 1 },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getPartnerById: 'partners/getPartnerById',
    }),
    partnerId() {
      return this.$route.params.id;
    },
    partner() {
      return this.getPartnerById(this.partnerId);
    },
    partnerOrders() {
      return this.partner?.orders ?? [];
    },
    columns() {
      return [
        {
          label: '№',
          field: 'id',
          tdClass: 'align-middle',
          cell: ({ row }) => <div class="flex flex-col items-start">
            <b>{ _.get(row, 'firstServiceCategory.abbreviation') }-{ row.id }</b>
            <StatusBadge status={row.state}/>
            { row.isComplex && <span class="text-10px leading-12px font-bold text-primary">Комплексный</span> }
          </div>,
          sortable: true,
          getSortBy: (row) => Number(row.id),
        },
        {
          label:
            'Создан',
          field: 'created_at',
          tdClass: 'align-middle',
          cell: ({ row }) => {
            if (!row.createdAt) {
              return null;
            }
            const date = this.$moment.utc(row.createdAt, 'YYYY-MM-DD HH:mm:ss').local();
            return (
              <div class="flex flex-col">
                <b>{ date.format('DD.MM.YYYY') }</b>
                <b>{ date.format('HH:mm:ss') }</b>
              </div>
            );
          },
          sortable: true,
          getSortBy: (row) => {
            if (!row.createdAt) {
              return 0;
            }
            return this.$moment.utc(row.createdAt, 'YYYY-MM-DD HH:mm:ss').format('X');
          },
        },
        {
          label:
            'Назначен',
          field: 'assigned_at',
          tdClass: 'align-middle',
          cell: ({ row }) => {
            if (!row.assignedAt) {
              return null;
            }
            const date = this.$moment.utc(row.assignedAt, 'YYYY-MM-DD HH:mm:ss').local();
            return (
              <div class="flex flex-col">
                <b>{ date.format('DD.MM.YYYY') }</b>
                <b>{ date.format('HH:mm:ss') }</b>
              </div>
            );
          },
          sortable: true,
          getSortBy: (row) => {
            if (!row.assignedAt) {
              return 0;
            }
            return this.$moment.utc(row.assignedAt, 'YYYY-MM-DD HH:mm:ss').format('X');
          },
        },
        {
          label:
            'Завершен',
          field: 'closed_at',
          tdClass: 'align-middle',
          cell: ({ row }) => {
            if (row.closedAt) {
              const date = this.$moment(row.closedAt, 'YYYY-MM-DD HH:mm:ss');
              return (
                <div class="flex flex-col">
                  <b>{ date.format('DD.MM.YYYY') }</b>
                  <b>{ date.format('HH:mm:ss') }</b>
                </div>
              );
            }
            return null;
          },
          sortable: true,
          getSortBy: (row) => {
            if (!row.closedAt) {
              return 0;
            }
            return this.$moment.utc(row.closedAt, 'YYYY-MM-DD HH:mm:ss').format('X');
          },
        },
        {
          label: 'Стоимость',
          field: 'price',
          tdClass: 'align-middle',
          cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.price)).format('0,0 $') }</b>,
          sortable: true,
          getSortBy: (row) => Number(row.price),
        },
        {
          label: 'Локация',
          field: 'region_name',
          tdClass: 'align-middle',
          cell: ({ row }) => <span>{ row.regionName }</span>,
          sortable: true,
          getSortBy: (row) => row.regionName,
        },
        {
          label: 'Доход партнера',
          field: 'partner_price',
          tdClass: 'align-middle',
          cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.partner_price)).format('0,0 $') }</b>,
          sortable: true,
          getSortBy: (row) => Number(row.partner_price),
        },
        {
          label: '% партнера',
          field: 'partner_percent',
          tdClass: 'align-middle',
          cell: ({ row }) => <span>{row.partner_percent}</span>,
          sortable: false,
        },
        {
          label: 'Исполнитель',
          field: 'contractor_name',
          tdClass: 'align-middle',
          cell: ({ row }) => <span>{row.contractorName}</span>,
          sortable: true,
          getSortBy: (row) => row.contractor_name,
        },
        {
          label: 'Услуга',
          field: 'firstServiceCategoryId',
          cell: ({ row }) => {
            const id = _.get(row, 'firstServiceCategory.iconAttach');
            return <Attachment class="w-6 h-6 object-center object-contain" id={id}/>;
          },
          sortable: true,
        },
      ];
    },
    partnerOrdersSorted() {
      const iteratees = this.sort.map((s) => {
        const { column } = s ?? {};
        if (!column) {
          return 0;
        }
        const getSortBy = this.columns.find((c) => c?.field === column)?.getSortBy;
        if (typeof getSortBy === 'function') {
          return (e) => getSortBy(e);
        }
        return column;
      });
      const orders = this.sort.map((s) => {
        const { descending } = s ?? {};
        return descending ? 'desc' : 'asc';
      });
      console.log({ iteratees, orders, sort: this.sort });
      return _.orderBy(this.partnerOrders, iteratees, orders);
    },
  },
  methods: {
    onRowClick({ row: event, event: e }) {
      e.stopPropagation();
      this.$router.push({
        name: 'orders.info',
        params: {
          id: event.id,
        },
      });
    },
    getRowStyleClass(row) {
      const classes = ['cursor-pointer'];
      if (row.priority) {
        classes.push('bg-primary-light bg-opacity-50');
      }
      return classes.join(' ');
    },
    onSortChange(e) {
      this.sort = e ?? [];
    },
  },
};
</script>
